import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, LogOut, Search, User, X } from 'lucide-react';
import { AppointmentCard } from '../components/AppointmentCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../hooks/useAuth';
import { Appointment, Client } from '../types';

interface DashboardAppointmentRecord {
  Clientes: {
    Nombres: string;
    Apellidos: string;
    Tel?: string;
    Email?: string;
  };
  ClientesDeHoy: {
    ID?: string;
    ROWID?: string;
    ID_Cliente: string;
    Hora: string;
    Status: Appointment['status'];
    Fecha: string;
    NombreTratamiento?: string;
  };
}

interface DashboardSearchRecord {
  Clientes: {
    ROWID: string;
    Nombres: string;
    Apellidos: string;
    Tel?: string;
    Email?: string;
  };
}

function mapAppointmentCardData(raw: DashboardAppointmentRecord): { appointment: Appointment; client: Client } {
  return {
    client: {
      id: raw.ClientesDeHoy.ID_Cliente,
      firstName: raw.Clientes.Nombres,
      lastName: raw.Clientes.Apellidos,
      phone: raw.Clientes.Tel || '',
      email: raw.Clientes.Email || ''
    },
    appointment: {
      id: raw.ClientesDeHoy.ID || raw.ClientesDeHoy.ROWID || raw.ClientesDeHoy.ID_Cliente,
      clientId: raw.ClientesDeHoy.ID_Cliente,
      time: raw.ClientesDeHoy.Hora,
      status: raw.ClientesDeHoy.Status,
      date: raw.ClientesDeHoy.Fecha,
      treatments: raw.ClientesDeHoy.NombreTratamiento ? JSON.parse(raw.ClientesDeHoy.NombreTratamiento) : []
    }
  };
}

function mapSearchClient(raw: DashboardSearchRecord): Client {
  return {
    id: raw.Clientes.ROWID,
    firstName: raw.Clientes.Nombres,
    lastName: raw.Clientes.Apellidos,
    phone: raw.Clientes.Tel || '',
    email: raw.Clientes.Email || ''
  };
}

export function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [selectedDate] = useState(new Date());
  const [listaClientes, setListaClientes] = useState<DashboardAppointmentRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<DashboardSearchRecord[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  const loadAppointments = async () => {
    const response = await fetch('/server/fichas_cliente_sisters_peluqueros_function/listar-clientes');
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'No se pudieron cargar las citas');
    }

    setListaClientes(data);
  };

  const handleAppointmentClick = (clientId: string) => {
    navigate(`/client/${clientId}`);
  };

  const handleLogout = () => {
    logout();
  };

  const handleRefreshAppointments = async () => {
    try {
      setIsRefreshing(true);
      setError(null);

      const response = await fetch('/server/fichas_cliente_sisters_peluqueros_function/refresh-citas', {
        method: 'POST'
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudieron actualizar las citas');
      }

      setListaClientes(data.appointments || []);
    } catch (refreshError) {
      console.error('Error al actualizar citas:', refreshError);
      setError(refreshError instanceof Error ? refreshError.message : 'No se pudieron actualizar las citas');
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadAppointments()
      .then(() => setError(null))
      .catch(loadError => {
        console.error('Error al obtener clientes:', loadError);
        setError(loadError instanceof Error ? loadError.message : 'No se pudieron cargar las citas');
      });
  }, []);

  useEffect(() => {
    const normalizedSearch = searchTerm.trim();

    if (!normalizedSearch) {
      setSearchResults([]);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = window.setTimeout(async () => {
      try {
        setIsSearching(true);
        setSearchError(null);

        const response = await fetch(
          `/server/fichas_cliente_sisters_peluqueros_function/buscar-clientes?q=${encodeURIComponent(normalizedSearch)}`,
          { signal: controller.signal }
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'No se pudieron buscar clientes');
        }

        setSearchResults(data);
      } catch (searchRequestError) {
        if (controller.signal.aborted) {
          return;
        }

        console.error('Error al buscar clientes:', searchRequestError);
        setSearchError(
          searchRequestError instanceof Error ? searchRequestError.message : 'No se pudieron buscar clientes'
        );
        setSearchResults([]);
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    }, 300);

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  const isShowingSearchResults = searchTerm.trim().length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {isRefreshing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-[#E91E8C]/20 border-t-[#E91E8C]" />
            <h2 className="text-lg font-semibold text-[#2d2d2d]">Actualizando las citas de hoy</h2>
            <p className="mt-2 text-sm text-gray-600">
              Espera un momento mientras sincronizamos las citas con SimplyBook.
            </p>
          </div>
        </div>
      )}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl text-[#2d2d2d] mb-1">Sisters Peluqueros</h1>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User className="w-4 h-4" />
                <span>{user?.name || user?.email || 'Administrador'}</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-gray-600 hover:text-[#E91E8C]"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-gradient-to-r from-[#E91E8C] to-[#FF69B4] rounded-2xl shadow-lg p-6 mb-6 text-white">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6" />
              <h2 className="text-2xl">Citas de Hoy</h2>
            </div>
            <button
              type="button"
              onClick={handleRefreshAppointments}
              disabled={isRefreshing}
              className="text-sm font-medium underline underline-offset-4 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isRefreshing ? 'Actualizando listado de citas...' : 'Actualizar listado de citas'}
            </button>
          </div>
          <p className="text-lg opacity-90">
            {selectedDate.toLocaleDateString('es-ES', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>

        {error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar clientes por nombre, email o telefono"
              className="pl-9 pr-10"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Limpiar busqueda"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <p className="mt-3 text-sm text-gray-500">
            {isShowingSearchResults
              ? 'Mostrando clientes encontrados en la base de datos.'
              : 'Sin busqueda activa se mantiene el listado habitual de citas de hoy.'}
          </p>
        </div>

        {searchError && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
            <p className="text-red-500">{searchError}</p>
          </div>
        )}

        <div className="space-y-4">
          {isShowingSearchResults ? (
            isSearching ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Buscando clientes...</p>
              </div>
            ) : searchResults.length > 0 ? (
              searchResults.map((item) => (
                <AppointmentCard
                  key={item.Clientes.ROWID}
                  client={mapSearchClient(item)}
                  onClick={() => handleAppointmentClick(item.Clientes.ROWID)}
                />
              ))
            ) : (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No se encontraron clientes para "{searchTerm.trim()}"</p>
              </div>
            )
          ) : listaClientes.length > 0 ? (
            listaClientes.map((item, index) => {
              const { appointment, client } = mapAppointmentCardData(item);

              return (
                <AppointmentCard
                  key={item.ClientesDeHoy.ROWID || item.ClientesDeHoy.ID || index}
                  appointment={appointment}
                  client={client}
                  onClick={() => handleAppointmentClick(item.ClientesDeHoy.ID_Cliente)}
                />
              );
            })
          ) : (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No hay citas programadas para hoy</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
